import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable, Subject, BehaviorSubject } from 'rxjs';

import { PlantNetworkStatus } from './plant-network-status.models';
import * as fromPlantNetwork from './plant-network-status.selectors';
import {
  dateChanged,
  fetchPlants,
  getLatestPlantNetworkStatus,
  getLatestPlantsNetworkStatus,
  updatePlantStatus,
} from './plant-network-status.actions';
import { take } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class PlantNetworkStatusFacade {
  // Selectors as observables
  plants$ = this.store.select(fromPlantNetwork.selectAllPlants);
  isLoading$ = this.store.select(fromPlantNetwork.selectIsLoading);
  error$ = this.store.select(fromPlantNetwork.selectError);
  currentDate$ = this.store.select(fromPlantNetwork.selectCurrentDate);
  statistics$ = this.store.select(fromPlantNetwork.selectPlantStatistics);
  // Private Subject to emit plant status changes
  private _plantStatusChanged = new Subject<PlantNetworkStatus>();

  // Public observable that components and services can subscribe to
  plantStatusChanged$ = this._plantStatusChanged.asObservable();

  // Dictionary to track loading status for each plant
  private _plantLoadingStatus: { [plantCode: string]: BehaviorSubject<boolean> } = {};

  // Dictionary to store the latest statuses obtained from plants
  private _latestPlantStatus: { [plantCode: string]: Subject<boolean> } = {};

  constructor(private store: Store) {}

  /**
   * Loads all plants from IndexedDB
   */
  loadPlants(): void {
    this.store.dispatch(fetchPlants());
  }

  /**
   * Updates the online/offline status of a plant
   */
  updatePlantStatus(plantCode: string, isOnline: boolean): void {
    this.store.dispatch(
      updatePlantStatus({
        payload: { PlantCode: plantCode, IsOnline: isOnline },
      }),
    );

    this.getPlantByCode(plantCode)
      .pipe(take(1))
      .subscribe((plant) => {
        // If the status has changed, notify the change
        this._plantStatusChanged.next({
          PlantCode: plantCode,
          IsOnline: isOnline,
          TimeDifference: plant.TimeDifference,
        } as PlantNetworkStatus);
      });
  }

  /**
   * Updates the current date
   */
  dateChanged(currentDate: string): void {
    this.store.dispatch(dateChanged({ payload: currentDate }));
  }

  /**
   * Gets a specific plant by its code
   */
  getPlantByCode(plantCode: string): Observable<PlantNetworkStatus | null> {
    return this.store.select(fromPlantNetwork.selectPlantByCode(plantCode));
  }

  /**
   * Checks if a plant is online
   */
  isPlantOnline(plantCode: string): Observable<boolean> {
    return this.store.select(fromPlantNetwork.selectIsPlantOnline(plantCode));
  }

  /**
   * Gets all online plants
   */
  getOnlinePlants(): Observable<PlantNetworkStatus[]> {
    return this.store.select(fromPlantNetwork.selectOnlinePlants);
  }

  /**
   * Gets all offline plants
   */
  getOfflinePlants(): Observable<PlantNetworkStatus[]> {
    return this.store.select(fromPlantNetwork.selectOfflinePlants);
  }

  /**
   * Get the latest status of a plant
   * @param plantCode Código de la planta
   * @returns Observable que emite true si la planta está online, false si está offline
   */
  getLatestPlantStatus(plantCode: string): Observable<boolean> {
    // Initialize Subject if not exists
    if (!this._latestPlantStatus[plantCode]) {
      this._latestPlantStatus[plantCode] = new Subject<boolean>();
    }

    if (!this._plantLoadingStatus[plantCode]) {
      this._plantLoadingStatus[plantCode] = new BehaviorSubject<boolean>(false);
    }

    // Mark as loading
    this._plantLoadingStatus[plantCode].next(true);

    // Dispatch the action to get the latest status
    this.store.dispatch(getLatestPlantNetworkStatus({ plantCode }));

    // Return the observable from the Subject
    return this._latestPlantStatus[plantCode].asObservable();
  }

  /**
   * Send the latest known status of a plant
   * @param plantCode plant code
   * @param isOnline online/offline status
   */
  sendLatestPlantStatus(plantCode: string, isOnline: boolean, timeDifference: number): void {
    // Inicializar Subject if not exists
    if (!this._latestPlantStatus[plantCode]) {
      this._latestPlantStatus[plantCode] = new Subject<boolean>();
    }

    // Emit the new status
    this._latestPlantStatus[plantCode].next(isOnline);

    // Mark as not loading
    if (this._plantLoadingStatus[plantCode]) {
      this._plantLoadingStatus[plantCode].next(false);
    }

    // Notify change
    this._plantStatusChanged.next({
      PlantCode: plantCode,
      IsOnline: isOnline,
      TimeDifference: timeDifference,
    } as PlantNetworkStatus);
  }

  /**
   * Get the latest status of plants
   * @param plantCodes Códigos de las plantas
   * @returns Observable que emite true/false conforme la planta este online
   */
  getLatestPlantsStatus(plantCodes: string[]): Map<string, Observable<boolean>> {
    // Initialize Subject if not exists
    plantCodes.forEach((plantCode) => {
      if (!this._latestPlantStatus[plantCode]) {
        this._latestPlantStatus[plantCode] = new Subject<boolean>();
      }

      if (!this._plantLoadingStatus[plantCode]) {
        this._plantLoadingStatus[plantCode] = new BehaviorSubject<boolean>(false);
      }

      // Mark as loading
      this._plantLoadingStatus[plantCode].next(true);
    });

    // Dispatch the action to get the latest status
    this.store.dispatch(getLatestPlantsNetworkStatus({ plantCodes: plantCodes }));

    // Return the observable from the Subject
    const res = new Map<string, Observable<boolean>>();

    plantCodes.map((plantCode) => res.set(plantCode, this._latestPlantStatus[plantCode].asObservable()));

    return res;
  }
}
